import React, { useContext, useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { PaymentContext } from "../../../../lib/context/PaymentContext";
import { useStripeCheckoutQuery } from "../../../../rq/hooks/useQueries";
import StripePayment from "../../layouts/business-dashboard/HistoryOfPackage/Plans/StripePayment/StripePayment";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";
import { STRIPE } from "../../../../config/app";
import { usePackageSubscriptionMutation } from "../../../../rq/hooks/useEditProfile.mutation";
import { usePackagePlansQuery } from "../../../../rq/hooks/useQueries";
import CircularIndeterminate from "../../../../components/CircularIndeterminate/CircularIndeterminate";
import { AuthContext } from "../../../../lib/context/AuthContext";
import { Link } from "react-router-dom";
import { LoaderIcon } from "react-hot-toast";
import { Info } from "@mui/icons-material";

const stripePromise = loadStripe(STRIPE);

// Individual Plan Component
function CreditPurchasePlanCard({ identifier, plan }: any) {
  const { name, id, price, points, priceWithVat, isSubscription } = plan;
  const { t } = useTranslation();
  const packageProfits = [
    {
      id: 1,
      title: `${t("credits.advantages.one")}`,
    },
    {
      id: 2,
      title: `${t("credits.advantages.two")}`,
    },
    {
      id: 3,
      title: `${t("credits.advantages.three")}`,
    },
    {
      id: 4,
      title: `${t("credits.advantages.four")}`,
    },
  ];
  const { clientSecret, setClientSecret } = useContext(PaymentContext);

  const options = {
    clientSecret,
  };
  const [openPayModal, setOpenPayModal] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const {
    mutate: getSubscription,
    isLoading: getSubscriptionLoading,
    data: subscriptionData,
    isSuccess: subscriptionSuccess,
  } = usePackageSubscriptionMutation(id, {
    onSuccess: () => setOpenPayModal(true),
    onError: () => {
      setIsProcessing(false);
    },
  });

  const handleOpenModal = () => {
    setIsProcessing(true);
    if (isSubscription) {
      getSubscription(id);
    } else {
      setOpenPayModal(true);
    }
  };

  const { data, isLoading, isSuccess } = useStripeCheckoutQuery(id, {
    enabled: openPayModal && isSubscription === false,
  });

  useEffect(() => {
    if (subscriptionData) {
      setClientSecret(subscriptionData as string);
    }
    if (data) {
      setClientSecret(data);
    }
  }, [isSuccess, subscriptionSuccess]);

  // Reset processing state when modal opens and clientSecret is available
  useEffect(() => {
    if (openPayModal && clientSecret) {
      setIsProcessing(false);
    }
  }, [openPayModal, clientSecret]);

  const isPopular = name === "Pro";

  return (
    <>
      <div
        className={`relative rounded-lg p-6 ${
          isPopular ? "border-blue-500 bg-blue-50" : "border-gray-200"
        }`}
        style={{
          border: "1px solid",
          borderColor: isPopular ? "#487BD9" : "#E5E7EB",
        }}
      >
        {isPopular && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-blue-500 truncate text-white px-3 py-1 rounded-full text-sm font-medium">
              {t("most_popular")}
            </span>
          </div>
        )}

        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold mb-2">{name}</h3>
          <div className="mb-2">
            <span className="text-lg font-bold">€{price}</span>
            <span className="text-gray-600 text-sm">/{points}Cr.</span>
            <p className="text-xs text-gray-500">
              {t("credits.price_without_vat")}
            </p>
          </div>
        </div>

        <ul className="space-y-3 mb-6">
          {packageProfits.map((pF) => (
            <li key={pF.id} className="flex items-start gap-2">
              <span
                className={`flex items-center justify-center w-5 h-5 rounded-full mt-0.5 flex-shrink-0 ${
                  isPopular ? "bg-blue-100" : "bg-gray-100"
                }`}
              >
                <CheckIcon
                  fontSize="inherit"
                  sx={{
                    color: isPopular ? "#487BD9" : "#999A99",
                    fontSize: "14px",
                  }}
                />
              </span>
              <span className="text-xs text-gray-600">{pF.title}</span>
            </li>
          ))}
        </ul>

        <button
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
            isPopular
              ? "bg-blue-500 text-white hover:bg-blue-600"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          } ${isProcessing ? "opacity-75 cursor-not-allowed" : ""}`}
          onClick={handleOpenModal}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <LoaderIcon className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            t("choose_plan")
          )}
        </button>
      </div>

      {clientSecret && (!isLoading || !getSubscriptionLoading) && (
        <Elements stripe={stripePromise} options={options} key={clientSecret}>
          <StripePayment
            planId={id}
            openPayModal={openPayModal}
            setOpenPayModal={setOpenPayModal}
            price={price}
            points={points}
            priceWithVat={priceWithVat}
          />
        </Elements>
      )}
    </>
  );
}

// Main Component
export default function CreditsPurchase() {
  const [checked, setChecked] = useState(false);
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const { data: packages = [], isLoading } = usePackagePlansQuery();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const subscriptionPackages = packages.filter(
    (pack) => pack.isSubscription === true
  );

  const creditsPackages = packages.filter(
    (pack) => pack.isSubscription === false
  );

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white font-montserrat min-h-screen flex flex-col justify-center items-center">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-light mb-4">
          <span className="text-blue-300">{t("credits.credits")}</span>{" "}
          {t("credits.purchase")}
        </h1>
        <div className="bg-gray-50 flex flex-col relative justify-center items-center gap-2 p-4 rounded-lg mb-6">
          <div className="absolute -top-4 flex justify-center left-0 right-0">
            <Info className="text-[#6095f6] text-4xl" />
          </div>
          <h5 className="font-medium text-lg">{t("credits.overview_title")}</h5>
          <p className="text-gray-700 mb-2">
            {t("credits.overview_description")}
          </p>
          <p className="text-gray-600">
            Contact:{" "}
            <a href="mailto:info@myjob.be" className="text-blue-500 underline">
              info@myjob.be
            </a>
          </p>
        </div>

        <div className="flex md:flex-row flex-col items-center justify-center gap-4 mb-8">
          <span className={`${!checked ? "text-gray-900" : "text-gray-500"}`}>
            {t("buy_through_packs")}
          </span>
          <div
            className={`relative w-12 h-6 rounded-full cursor-pointer ${
              checked ? "bg-blue-500" : "bg-gray-300"
            }`}
            onClick={() => setChecked(!checked)}
            style={{ transition: "background-color 0.3s" }}
          >
            <div
              className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow ${
                checked ? "left-7" : "left-1"
              }`}
              style={{
                transition:
                  "left 0.3s cubic-bezier(0.4,0,0.2,1), box-shadow 0.3s",
              }}
            />
          </div>
          <span className={`${checked ? "text-gray-900" : "text-gray-500"}`}>
            {t("buy_through_sub")}
          </span>
        </div>

        {checked && user?.userSubscription && (
          <p className="font-bold text-lg text-gray-800 mb-4">
            {t("already_have_subscription")}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center">
          <CircularIndeterminate />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {checked &&
            !user?.userSubscription &&
            subscriptionPackages.map((plan: any) => (
              <CreditPurchasePlanCard
                key={plan.id}
                plan={plan}
                identifier={plan.id}
              />
            ))}
          {!checked &&
            creditsPackages.map((plan: any) => (
              <CreditPurchasePlanCard
                key={plan.id}
                plan={plan}
                identifier={plan.id}
              />
            ))}
        </div>
      )}

      <div className="text-center">
        <Link
          to="/home"
          className="text-gray-600 hover:text-gray-800 underline"
        >
          {t("choose_later")}
        </Link>
      </div>
    </div>
  );
}
